import { Spacing } from '@persona-id/design-tokens';
import { FilterIcon } from '@persona-id/design-tokens/icons';
import { FC } from 'react';
import styled from 'styled-components';

import { VerificationTemplateFilter } from 'dashboard/components/VerificationAnalytics';
import { TableNullState } from 'dashboard/layouts/TableLayout';
import { VerificationStatusSummary } from 'dashboard/models/metrics-verification';
import { FilterRule } from 'dashboard/views/DashboardVerificationAnalyticsVolumeView/BaseVerificationFilterRow';
import {
  FILTER_TYPES,
  OptionsContainer,
} from 'dashboard/views/DashboardVerificationAnalyticsVolumeView/MetricsCard';
import MetricsSummary from 'dashboard/views/DashboardVerificationAnalyticsVolumeView/MetricsSummary';
import {
  SectionTitle,
  StyledMetricsCard,
  SummaryCardBody,
} from 'dashboard/views/DashboardVerificationAnalyticsVolumeView/style';
import { VerificationComposedCardBody as ComposedCardSection } from 'dashboard/views/DashboardVerificationAnalyticsVolumeView/VerificationMetricsChart';
import Button from 'design-system/components/Button';
import { VerificationType } from 'lib/interfaces/api';

interface EmptyStateProps {
  verificationTypeTitle: string;
  emptyData: VerificationStatusSummary;
  verificationType: VerificationType;
  handleOpenFilters: () => void;
  filterOrRulesForType: FilterRule[][];
  verificationTemplates: { name: string; id: string; tooltipText?: string }[];
  handleVerificationTemplateFilterUpdate: (
    verificationType: VerificationType,
    allVTemplatesIdsForType: string[],
  ) => void;
  verificationTemplateFilterRules: string[];
}

const StyledButton = styled(Button)`
  padding: ${Spacing[2]} ${Spacing[3]} ${Spacing[2]} ${Spacing[2]};
  margin: ${Spacing[2]};
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

export const EmptyState: FC<EmptyStateProps> = ({
  verificationTypeTitle,
  emptyData,
  verificationType,
  handleOpenFilters,
  filterOrRulesForType,
  verificationTemplates,
  handleVerificationTemplateFilterUpdate,
  verificationTemplateFilterRules,
}) => {
  const shouldDisplayFiltersForType = FILTER_TYPES.includes(verificationType);
  const hasValidFilterOrRulesForType =
    filterOrRulesForType.length > 0 &&
    !(
      filterOrRulesForType.length === 1 &&
      (filterOrRulesForType[0].length === 0 || Object.keys(filterOrRulesForType[0][0]).length === 0)
    );

  const filterRulesCount = hasValidFilterOrRulesForType ? ` (${filterOrRulesForType.length})` : '';
  const filterButtonCopy = `Filters${filterRulesCount}`;

  return (
    <StyledMetricsCard key={`${verificationTypeTitle}-empty-state`}>
      <SummaryCardBody>
        <HeaderContainer>
          <SectionTitle data-test='verification-type-metrics-card--name'>
            {verificationTypeTitle}
          </SectionTitle>
          <OptionsContainer>
            {shouldDisplayFiltersForType && (
              <>
                <VerificationTemplateFilter
                  handleVerificationTemplateFilterUpdate={handleVerificationTemplateFilterUpdate}
                  verificationTemplateFilterRules={verificationTemplateFilterRules}
                  verificationTemplates={verificationTemplates ?? []}
                  verificationType={verificationType}
                />
                <StyledButton
                  type='button'
                  variant='basic'
                  width='auto'
                  prefixIcon={<FilterIcon />}
                  onClick={handleOpenFilters}
                >
                  {filterButtonCopy}
                </StyledButton>
              </>
            )}
          </OptionsContainer>
        </HeaderContainer>
        <MetricsSummary data={emptyData} />
        <ComposedCardSection>
          <TableNullState dataTestBlock='verifications-volume-view'>
            <div data-test={`${verificationTypeTitle}-empty-state-copy`}>
              No matched results. Try adjusting your filters to view data.
            </div>
          </TableNullState>
        </ComposedCardSection>
      </SummaryCardBody>
    </StyledMetricsCard>
  );
};
